import { Box, Button, Typography } from "@mui/material";
import { GridViewOutlined } from "@mui/icons-material";

const LAPTOP_FIT_QUERY = "@media (min-width: 1280px) and (min-height: 768px)";
const STANDARD_LAPTOP_HEIGHT_QUERY = "@media (min-width: 1280px) and (min-height: 768px) and (max-height: 900px)";

export const FEATURE_STUDIO_WORKFLOW_CARD_HEIGHT_SX = {
    minHeight: 181,
    [LAPTOP_FIT_QUERY]: { minHeight: 212, maxHeight: 212 },
    [STANDARD_LAPTOP_HEIGHT_QUERY]: { minHeight: 188, maxHeight: 188 }
} as const;

export const PRODUCT_HUB_WORKFLOW_CARD_HEIGHT_SX = {
    minHeight: 148
} as const;

const hierarchyDescriptionSx = {
    flex: 1,
    minWidth: 0,
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#64748b"
} as const;

const ProductCubeIcon = ({ size = 20, iconSize = 12 }: { size?: number; iconSize?: number }) => (
    <Box
        component="span"
        aria-hidden="true"
        sx={{
            width: size,
            height: size,
            flex: `0 0 ${size}px`,
            borderRadius: "6px",
            bgcolor: "#6366f1",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center"
        }}
    >
        <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
    </Box>
);

const FeatureLayersIcon = ({ size = 18, iconSize = 10 }: { size?: number; iconSize?: number }) => (
    <Box
        component="span"
        aria-hidden="true"
        sx={{
            width: size,
            height: size,
            flex: `0 0 ${size}px`,
            borderRadius: "5px",
            bgcolor: "#60a5fa",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center"
        }}
    >
        <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
        </svg>
    </Box>
);

interface FeatureStudioHierarchyCardProps {
    onOpenHierarchy?: () => void;
    compact?: boolean;
}

/** Shared Product → Feature → User Story hierarchy card used in Feature Studio and Product Hub. */
export const FeatureStudioHierarchyCard = ({ onOpenHierarchy, compact = false }: FeatureStudioHierarchyCardProps) => {
    return (
        <Box
            data-testid="feature-studio-hierarchy-card"
            sx={{
                minWidth: 0,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                flex: 1,
                ...(compact ? PRODUCT_HUB_WORKFLOW_CARD_HEIGHT_SX : FEATURE_STUDIO_WORKFLOW_CARD_HEIGHT_SX),
                border: "1px solid rgba(15,23,42,.10)",
                borderRadius: "10px",
                bgcolor: "background.paper",
                p: compact ? 0.875 : 1.25,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                [LAPTOP_FIT_QUERY]: compact ? { p: 0.875 } : { ...FEATURE_STUDIO_WORKFLOW_CARD_HEIGHT_SX[LAPTOP_FIT_QUERY], p: 1, overflow: "hidden" }
            }}
        >
            <Box sx={{ flex: 1, minHeight: 0, width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Box
                    sx={{
                        width: "100%",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        p: compact ? 0.375 : 0.75,
                        [LAPTOP_FIT_QUERY]: { gap: 0.75, p: 0.5 }
                    }}
                >
                    <ProductCubeIcon />
                    <Box sx={{ minWidth: 0, width: "100%" }}>
                        <Typography
                            component="span"
                            sx={{
                                flex: "0 0 auto",
                                display: "block",
                                fontSize: 12.5,
                                lineHeight: 1.4,
                                fontWeight: 600,
                                color: "#0f172a",
                                [LAPTOP_FIT_QUERY]: { fontSize: 12.5, whiteSpace: "nowrap" }
                            }}
                        >
                            Product
                        </Typography>
                        <Typography
                            component="span"
                            title="Define the high-level functionality and requirements for the overall product."
                            sx={{
                                ...hierarchyDescriptionSx,
                                fontSize: 11,
                                lineHeight: 1.4,
                                [LAPTOP_FIT_QUERY]: { fontSize: 10.25, lineHeight: 1.2 }
                            }}
                        >
                            Define the high-level functionality and requirements for the overall product.
                        </Typography>
                    </Box>
                </Box>

                <Box
                    data-testid="feature-hierarchy-box"
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        width: "calc(100% - 6px)",
                        boxSizing: "border-box",
                        ml: "6px",
                        mt: 0.5,
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #dbe3ef",
                        borderRadius: 1.5,
                        bgcolor: "#f1f5f9",
                        overflow: "hidden"
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            boxSizing: "border-box",
                            display: "flex",
                            flexShrink: 0,
                            alignItems: "flex-start",
                            gap: 1,
                            p: 0.5,
                            [LAPTOP_FIT_QUERY]: { gap: 0.75 }
                        }}
                    >
                        <FeatureLayersIcon />
                        <Box sx={{ minWidth: 0, width: "100%" }}>
                            <Typography
                                component="span"
                                sx={{
                                    flex: "0 0 auto",
                                    display: "block",
                                    fontSize: 11.5,
                                    lineHeight: 1.35,
                                    fontWeight: 600,
                                    color: "#0f172a",
                                    [LAPTOP_FIT_QUERY]: { fontSize: 12 }
                                }}
                            >
                                Feature
                            </Typography>
                            <Typography
                                component="span"
                                title="Develop specific product functionality with specific designs and requirements."
                                sx={{
                                    ...hierarchyDescriptionSx,
                                    fontSize: 10.5,
                                    lineHeight: 1.35,
                                    [LAPTOP_FIT_QUERY]: { fontSize: 10.25, lineHeight: 1.2 }
                                }}
                            >
                                Develop specific product functionality with specific designs and requirements.
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        data-testid="user-story-hierarchy-box"
                        sx={{
                            flex: `1 1 ${compact ? 42 : 48}px`,
                            minHeight: compact ? 38 : 40,
                            maxHeight: compact ? 42 : 48,
                            width: "calc(100% - 12px)",
                            boxSizing: "border-box",
                            ml: "6px",
                            mt: 0.75,
                            mb: "6px",
                            display: "flex",
                            alignItems: "flex-start",
                            border: "1px solid #dbe3ef",
                            borderRadius: 1.25,
                            bgcolor: "#f8fafc",
                            overflow: "hidden"
                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                boxSizing: "border-box",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                                p: 0.5,
                                [LAPTOP_FIT_QUERY]: { gap: 0.75 }
                            }}
                        >
                            <GridViewOutlined
                                sx={{
                                    p: 0.35,
                                    borderRadius: 1,
                                    bgcolor: "#334155",
                                    color: "white",
                                    fontSize: 20,
                                    [LAPTOP_FIT_QUERY]: { p: 0.25, fontSize: 18 }
                                }}
                            />
                            <Box sx={{ minWidth: 0, width: "100%" }}>
                                <Typography
                                    component="span"
                                    sx={{
                                        flex: "0 0 auto",
                                        display: "block",
                                        fontSize: 11,
                                        lineHeight: 1.3,
                                        fontWeight: 600,
                                        color: "#0f172a",
                                        [LAPTOP_FIT_QUERY]: { fontSize: 11.75 }
                                    }}
                                >
                                    User Story
                                </Typography>
                                <Typography
                                    component="span"
                                    title="All task assignment and code generation occur at the Story level."
                                    sx={{
                                        ...hierarchyDescriptionSx,
                                        fontSize: 10,
                                        lineHeight: 1.3,
                                        [LAPTOP_FIT_QUERY]: { fontSize: 10.25, lineHeight: 1.2 }
                                    }}
                                >
                                    All task assignment and code generation occur at the Story level.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {onOpenHierarchy && (
                <Box data-testid="hierarchy-card-action" sx={{ flexShrink: 0, mt: "auto", pt: "10px", [LAPTOP_FIT_QUERY]: { pt: "6px", pb: "2px" } }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onOpenHierarchy}
                        fullWidth
                        sx={{
                            height: 32,
                            minHeight: 32,
                            border: "1px solid #6366f1",
                            borderRadius: "7px",
                            bgcolor: "transparent",
                            color: "#4f46e5",
                            fontFamily: "inherit",
                            fontSize: 11.5,
                            fontWeight: 650,
                            lineHeight: 1,
                            textTransform: "none",
                            boxSizing: "border-box",
                            [LAPTOP_FIT_QUERY]: { height: 28, minHeight: 28, fontSize: 11 },
                            "&:hover": { border: "1px solid #6366f1", bgcolor: "transparent" }
                        }}
                    >
                        View Product/Feature/Story List
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default FeatureStudioHierarchyCard;
